/**
 * 需要想定 (単日) の編集単位。一括想定の小売は小売そのもの、グループ別の小売は各需要グループと
 * その他 (葉) を、同じ並びの編集行として持つ。グループ別の小売の値は葉の足し算で、行としては
 * 持たない (小売と葉を同時に足して二重に数えない)。小売の値を読む箇所は `retailerTotals` /
 * `retailerSlotsOf` を通す。
 *
 * どの小売がグループ別か・一括に戻してグループの合計を引き継いでいるかは backend の応答
 * (`RetailerDraft.byGroup` / `carriedOverFromGroups`) を写すだけで、ここでは決めない。
 */
import { create } from '@bufbuild/protobuf'
import {
  type DemandForecastLeafKey,
  DemandForecastLeafKeySchema,
  type RetailerDraft,
  type RetailerLeafDraft,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

import { protoRetailerToEditRow } from '~/pages/DemandForecastDaily/models/editRow'
import { BREAKDOWN_TOTAL_TAB } from '~/pages/DemandForecastDaily/models/retailerBreakdownView'
import {
  calcBgTotalSlots,
  protoSlotsToKoma,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type {
  KomaSlots,
  RetailerEditRow,
  RetailerFieldsSnapshot,
} from '~/pages/DemandForecastDaily/models/types'

/** 葉: 需要グループ 1 つ、またはその他。 */
export type LeafRef =
  | { kind: 'group'; demandGroupId: string }
  | { kind: 'residual' }

/** 編集行が何を保存するか。小売コードは行 (`RetailerEditRow.retailerBusinessCd`) が持つ。 */
export type EditUnit = { kind: 'retailer' } | { kind: 'leaf'; leaf: LeafRef }

/**
 * 編集行の key (editRows・保存状態の key)。一括の小売は小売コードのまま、葉は
 * `leaf:<小売>:<group id | residual>`。
 */
export function editUnitKey(
  retailerBusinessCd: string,
  unit: EditUnit,
): string {
  if (unit.kind === 'retailer') return retailerBusinessCd
  return `leaf:${retailerBusinessCd}:${unit.leaf.kind === 'group' ? unit.leaf.demandGroupId : 'residual'}`
}

function leafRefOf(key: RetailerLeafDraft['key']): LeafRef | undefined {
  switch (key.case) {
    case 'demandGroupId':
      return { kind: 'group', demandGroupId: key.value }
    case 'residual':
      return { kind: 'residual' }
    default:
      return undefined
  }
}

function leafToEditRow(
  retailer: RetailerDraft,
  leafDraft: RetailerLeafDraft,
  leaf: LeafRef,
): RetailerEditRow {
  const fields: RetailerFieldsSnapshot = {
    forecastSlots: protoSlotsToKoma(leafDraft.forecastSlots),
    providerSlots: protoSlotsToKoma(leafDraft.providerSlots),
    manualEditKomas: new Set(leafDraft.manualEditKomas),
    provider: leafDraft.provider,
    providerOther: leafDraft.providerOther,
    providerLabel: leafDraft.providerLabel,
  }
  return {
    retailerBusinessCd: retailer.retailerBusinessCd,
    displayName: retailer.displayName,
    unit: { kind: 'leaf', leaf },
    carriedOverFromGroups: false,
    ...fields,
    draftUpdatedAt: leafDraft.draftUpdatedAt,
    baseline: { ...fields, manualEditKomas: new Set(fields.manualEditKomas) },
  }
}

/**
 * page の小売を編集行 (key → 行) へ写す。グループ別の小売は葉の行だけ (小売の行は作らない)、
 * それ以外は小売の行 1 つ。key の無い葉 (この版が知らない形) は行にしない。
 */
export function editUnitsFromPage(
  retailers: readonly RetailerDraft[],
): Record<string, RetailerEditRow> {
  const rows: Record<string, RetailerEditRow> = {}
  for (const retailer of retailers) {
    if (retailer.byGroup === undefined) {
      rows[retailer.retailerBusinessCd] = protoRetailerToEditRow(retailer)
      continue
    }
    for (const leafDraft of retailer.byGroup.leaves) {
      const leaf = leafRefOf(leafDraft.key)
      if (leaf === undefined) continue
      const row = leafToEditRow(retailer, leafDraft, leaf)
      rows[editUnitKey(retailer.retailerBusinessCd, row.unit)] = row
    }
  }
  return rows
}

/**
 * 小売ごとの値 (小売コード → 48 コマ)。グループ別の小売は葉の足し算 (全葉が空のコマだけ空)。
 * 合計タブ・上限の判定が読む小売の値はここから取る。
 */
export function retailerTotals(
  rows: Record<string, RetailerEditRow>,
): Map<string, KomaSlots> {
  const byRetailer = retailerSlotLists(rows)
  return new Map(
    [...byRetailer].map(([retailerBusinessCd, slotsList]) => [
      retailerBusinessCd,
      calcBgTotalSlots(slotsList),
    ]),
  )
}

/** 小売ごとの元の編集系列。保存前の十進上限判定は丸めた合計を通さずこれを読む。 */
export function retailerSlotLists(
  rows: Record<string, RetailerEditRow>,
): Map<string, KomaSlots[]> {
  const byRetailer = new Map<string, KomaSlots[]>()
  for (const row of Object.values(rows)) {
    const list = byRetailer.get(row.retailerBusinessCd) ?? []
    list.push(row.forecastSlots)
    byRetailer.set(row.retailerBusinessCd, list)
  }
  return byRetailer
}

/** 1 つの小売の値。行が無い小売は undefined。 */
export function retailerSlotsOf(
  rows: Record<string, RetailerEditRow>,
  retailerBusinessCd: string,
): KomaSlots | undefined {
  return retailerTotals(rows).get(retailerBusinessCd)
}

/** 保存 request の保存先 (`SaveDemandForecastDraftRequest.leaf`)。小売は leaf を付けない。 */
export function saveTargetOf(unit: EditUnit): {
  leaf?: DemandForecastLeafKey
} {
  if (unit.kind === 'retailer') return {}
  return {
    leaf: create(DemandForecastLeafKeySchema, {
      key:
        unit.leaf.kind === 'group'
          ? { case: 'demandGroupId', value: unit.leaf.demandGroupId }
          : { case: 'residual', value: {} },
    }),
  }
}

/**
 * 選んだ小売とグループタブから、入力する編集行の key を返す。一括の小売は合計タブで小売の行、
 * グループ別の小売はタブの葉の行。グループ別の小売の合計タブと、タブの葉が page に無い時
 * (内訳と page の応答の間でモードが変わった時を含む) は undefined (入力できない)。
 * タブの key は `retailerBreakdownView` のもの (`group:<id>` / `residual` / `total`)。
 */
export function selectEditUnit(
  retailerBusinessCd: string,
  tabKey: string,
  retailers: readonly RetailerDraft[],
): string | undefined {
  const retailer = retailers.find(
    (r) => r.retailerBusinessCd === retailerBusinessCd,
  )
  if (retailer?.byGroup === undefined)
    return tabKey === BREAKDOWN_TOTAL_TAB ? retailerBusinessCd : undefined
  if (tabKey === BREAKDOWN_TOTAL_TAB) return undefined
  const leaf = retailer.byGroup.leaves
    .map((l) => leafRefOf(l.key))
    .find(
      (l) =>
        l !== undefined &&
        (l.kind === 'residual'
          ? tabKey === 'residual'
          : tabKey === `group:${l.demandGroupId}`),
    )
  return leaf === undefined
    ? undefined
    : editUnitKey(retailerBusinessCd, { kind: 'leaf', leaf })
}
